import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { SX_DESTAQUE, useRolarAteDestaque } from '@/hooks/useRolarAteDestaque'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import HistoryIcon from '@mui/icons-material/History'
import ReplyIcon from '@mui/icons-material/Reply'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { useSnackbar } from 'notistack'
import { pendenciaService } from '@/services/pendenciaService'
import { documentoService } from '@/services/documentoService'
import { DocumentoObrigatorio, TIPO_ENTIDADE_PENDENCIA, tipoDocumentoParaUpload } from '@/utils/docsObrigatorios'
import { oportunidadeService } from '@/services/oportunidadeService'
import type { Documento } from '@/types/documento'
import type {
  Pendencia,
  PendenciaInteracao,
  PendenciaStatus,
  ResponsavelPendencia,
} from '@/types/pendencia'
import { authStore } from '@/store/authStore'
import { ehConsultor } from '@/components/layout/menu'
import { VersoesDocumento } from '@/components/documentos/VersoesDocumento'
import { ArquivoSubstitutoDialog } from '@/components/documentos/ArquivoSubstitutoDialog'
import { mensagemErroDocumento } from '@/utils/mensagemErroDocumento'

// Espelha as transições validadas no backend (PendenciaStatus).
const TRANSICOES: Record<PendenciaStatus, PendenciaStatus[]> = {
  ABERTA: ['VISUALIZADA', 'RESPONDIDA', 'CANCELADA'],
  VISUALIZADA: ['RESPONDIDA', 'CANCELADA'],
  RESPONDIDA: ['EM_VALIDACAO', 'CANCELADA'],
  EM_VALIDACAO: ['RESOLVIDA', 'REABERTA'],
  RESOLVIDA: ['REABERTA'],
  REABERTA: ['RESPONDIDA', 'CANCELADA'],
  CANCELADA: [],
}

const STATUS_COR: Record<PendenciaStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  ABERTA: 'warning',
  VISUALIZADA: 'info',
  RESPONDIDA: 'info',
  EM_VALIDACAO: 'warning',
  RESOLVIDA: 'success',
  REABERTA: 'warning',
  CANCELADA: 'default',
}

const STATUS_LABEL: Record<PendenciaStatus, string> = {
  ABERTA: 'Aberta',
  VISUALIZADA: 'Visualizada',
  RESPONDIDA: 'Respondida',
  EM_VALIDACAO: 'Em validação',
  RESOLVIDA: 'Resolvida',
  REABERTA: 'Reaberta',
  CANCELADA: 'Cancelada',
}

/**
 * Pendência "aberta" para o arquivo substituto (Onda 4, L11/Q38) — espelha
 * `DocumentoAcessoPolicy.STATUS_PENDENCIA_ABERTA` no backend: antes de a curadoria começar a
 * validar. Em validação, resolvida ou cancelada, só a curadoria reabre.
 */
const STATUS_ACEITA_SUBSTITUTO: PendenciaStatus[] = ['ABERTA', 'VISUALIZADA', 'RESPONDIDA', 'REABERTA']

const fmtData = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—')

const PendenciasPage: FC = () => {
  const { id: reservaId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)

  // Criar e transicionar pendências são atos do back-office. Persona pelo critério único do
  // menu (como no Financeiro): CONSULTOR com OPERADOR residual é consultor e NÃO opera
  // (VC-09, Onda 2 / DEV-4) — o backend também recusa, a tela só espelha.
  const podeOperar = useMemo(
    () =>
      !ehConsultor(usuario?.permissoes) &&
      !!usuario?.permissoes?.some((p) => ['ADMIN', 'GERENTE', 'OPERADOR'].includes(p.toUpperCase())),
    [usuario],
  )

  const [pendencias, setPendencias] = useState<Pendencia[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  // Deep link da Central (L7 / Q40, `?pendencia=<id>`): destaca e rola até a pendência. A
  // lista vem do GET escopado da reserva — id de fora dela simplesmente não destaca nada.
  const [searchParams] = useSearchParams()
  const pendenciaDestacada = searchParams.get('pendencia')
  useRolarAteDestaque(
    pendenciaDestacada && pendencias.some((p) => p.id === pendenciaDestacada)
      ? `pendencia-${pendenciaDestacada}`
      : null,
    !carregando
  )

  // criar
  const [criarAberto, setCriarAberto] = useState(false)
  const [novoTitulo, setNovoTitulo] = useState('')
  const [novaDescricao, setNovaDescricao] = useState('')
  const [novoDocumento, setNovoDocumento] = useState('')
  const [novoResponsavel, setNovoResponsavel] = useState<ResponsavelPendencia>('CONSULTOR')
  const [novoPrazo, setNovoPrazo] = useState('')

  // responder
  const [responderAlvo, setResponderAlvo] = useState<Pendencia | null>(null)
  const [textoResposta, setTextoResposta] = useState('')
  const [anexos, setAnexos] = useState<File[]>([])
  const [confirmaAtualizado, setConfirmaAtualizado] = useState(false)
  const [enviandoResposta, setEnviandoResposta] = useState(false)

  // Resposta tipada (Fase 6, item 21): o tipo vem da própria pendência. Com documento
  // solicitado, o que responde é o ANEXO (texto vira observação opcional); sem, o que
  // responde é o TEXTO. Nos dois, o consultor atesta que está atualizado antes de enviar.
  const tipoResposta: 'documento' | 'informacao' = responderAlvo?.documentoSolicitado
    ? 'documento'
    : 'informacao'
  const respostaPronta =
    confirmaAtualizado &&
    (tipoResposta === 'documento' ? anexos.length > 0 : textoResposta.trim().length > 0)

  // Anexos já enviados, indexados por pendência (o módulo de documentos é a fonte).
  const [anexosPorPendencia, setAnexosPorPendencia] = useState<Map<string, Documento[]>>(new Map())
  /** Checklist da reserva: casa o documento pedido na pendência com o tipo/exigência do upload. */
  const [checklist, setChecklist] = useState<DocumentoObrigatorio[]>([])

  // arquivo substituto (L11/Q38): a resposta anterior nunca é apagada, vira versão anterior
  const [substituindo, setSubstituindo] = useState<Documento | null>(null)
  const [enviandoSubstituto, setEnviandoSubstituto] = useState(false)

  // histórico
  const [historicoAlvo, setHistoricoAlvo] = useState<Pendencia | null>(null)
  const [interacoes, setInteracoes] = useState<PendenciaInteracao[]>([])

  const carregar = useCallback(async () => {
    if (!reservaId) return
    setCarregando(true)
    setErro(null)
    try {
      const lista = await pendenciaService.listarPorReserva(reservaId)
      setPendencias(lista)
      // Best effort: sem checklist o upload ainda resolve o tipo pelo nome do enum ou cai em OUTRO.
      oportunidadeService
        .documentosObrigatorios(reservaId)
        .then(setChecklist)
        .catch(() => setChecklist([]))

      // Anexos vêm do módulo de documentos (serviço separado): uma única busca por
      // tipoEntidade, indexada em memória. Se falhar, a tela segue sem os anexos.
      try {
        const page = await documentoService.listarFiltrado(
          TIPO_ENTIDADE_PENDENCIA,
          undefined,
          0,
          500,
        )
        const porPendencia = new Map<string, Documento[]>()
        const idsDaReserva = new Set(lista.map((p) => p.id))
        for (const d of page.content ?? []) {
          const ref = d.entidadeReferencia
          if (!ref || !idsDaReserva.has(ref)) continue
          porPendencia.set(ref, [...(porPendencia.get(ref) ?? []), d])
        }
        setAnexosPorPendencia(porPendencia)
      } catch {
        setAnexosPorPendencia(new Map())
      }
    } catch (e) {
      setErro('Não foi possível carregar as pendências desta reserva.')
    } finally {
      setCarregando(false)
    }
  }, [reservaId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const criar = async () => {
    if (!reservaId || !novoTitulo.trim()) return
    try {
      await pendenciaService.criar({
        oportunidadeId: reservaId,
        titulo: novoTitulo.trim(),
        descricao: novaDescricao || undefined,
        responsavel: novoResponsavel,
        documentoSolicitado: novoDocumento || undefined,
        prazoResposta: novoPrazo || undefined,
      })
      enqueueSnackbar('Pendência criada', { variant: 'success' })
      setCriarAberto(false)
      setNovoTitulo('')
      setNovaDescricao('')
      setNovoDocumento('')
      setNovoPrazo('')
      await carregar()
    } catch {
      enqueueSnackbar('Erro ao criar a pendência', { variant: 'error' })
    }
  }

  const fecharResposta = () => {
    setResponderAlvo(null)
    setTextoResposta('')
    setAnexos([])
    setConfirmaAtualizado(false)
  }

  const responder = async () => {
    if (!responderAlvo || !respostaPronta) return
    // Resposta de documento sem observação: o texto registra o que foi enviado — a
    // resposta continua obrigatória no backend, e o histórico fica legível.
    const texto = textoResposta.trim()
      || `Documento(s) enviado(s): ${anexos.map((a) => a.name).join(', ')}`
    setEnviandoResposta(true)
    try {
      // Anexos primeiro: se o upload falhar, a pendência NÃO é marcada como
      // respondida — evita "respondida sem o documento que foi pedido".
      // O documento pedido é texto livre; o upload exige o enum TipoDocumento (11/09: mandar
      // o texto cru dava 400 e a pendência nunca era respondida).
      const { tipo, exigenciaChave } = tipoDocumentoParaUpload(responderAlvo.documentoSolicitado, checklist)
      for (const arquivo of anexos) {
        await documentoService.upload(
          arquivo,
          tipo,
          responderAlvo.id,
          TIPO_ENTIDADE_PENDENCIA,
          exigenciaChave,
        )
      }

      await pendenciaService.responder(responderAlvo.id, texto, confirmaAtualizado)
      enqueueSnackbar(
        anexos.length > 0
          ? `Resposta enviada com ${anexos.length} anexo(s)`
          : 'Resposta enviada',
        { variant: 'success' },
      )
      fecharResposta()
      await carregar()
    } catch {
      enqueueSnackbar(
        anexos.length > 0
          ? 'Falha ao enviar os anexos — a pendência não foi respondida'
          : 'Erro ao responder a pendência',
        { variant: 'error' },
      )
    } finally {
      setEnviandoResposta(false)
    }
  }

  /** Só quem enviou troca o próprio anexo, e só com a pendência aberta — o backend confere de novo. */
  const podeSubstituir = (p: Pendencia, d: Documento) =>
    STATUS_ACEITA_SUBSTITUTO.includes(p.status) && !!usuario?.id && d.usuarioUpload === usuario.id

  const enviarSubstituto = async (arquivo: File, motivo: string) => {
    if (!substituindo) return
    setEnviandoSubstituto(true)
    try {
      await documentoService.substituir(substituindo.id, arquivo, motivo)
      enqueueSnackbar('Arquivo substituto enviado — o anterior ficou nas versões anteriores', { variant: 'success' })
      setSubstituindo(null)
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroDocumento(e, 'Não foi possível enviar o arquivo substituto'), { variant: 'error' })
    } finally {
      setEnviandoSubstituto(false)
    }
  }

  const transicionar = async (p: Pendencia, novo: PendenciaStatus) => {
    try {
      await pendenciaService.transicionar(p.id, novo)
      enqueueSnackbar(`Pendência: ${STATUS_LABEL[novo]}`, { variant: 'success' })
      await carregar()
    } catch {
      enqueueSnackbar('Erro ao atualizar o status', { variant: 'error' })
    }
  }

  const abrirHistorico = async (p: Pendencia) => {
    setHistoricoAlvo(p)
    try {
      setInteracoes(await pendenciaService.historico(p.id))
    } catch {
      setInteracoes([])
    }
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component="button" onClick={() => navigate('/reservas')} underline="hover">
          Reservas
        </Link>
        <Typography color="text.primary">Pendências</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Voltar">
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/reservas')} color="inherit">
              Voltar
            </Button>
          </Tooltip>
          <Typography variant="h5">Pendências da reserva</Typography>
        </Stack>
        {podeOperar && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCriarAberto(true)}>
            Nova pendência
          </Button>
        )}
      </Stack>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Card>
        <CardContent>
          {carregando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pendencias.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              Nenhuma pendência para esta reserva.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell>Responsável</TableCell>
                    <TableCell>Documento solicitado</TableCell>
                    <TableCell>Anexos</TableCell>
                    <TableCell>Prazo</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendencias.map((p) => {
                    const podeResponder = ['ABERTA', 'VISUALIZADA', 'REABERTA'].includes(p.status)
                    return (
                      <TableRow
                        key={p.id}
                        hover
                        id={`pendencia-${p.id}`}
                        data-destacado={p.id === pendenciaDestacada ? 'true' : undefined}
                        sx={p.id === pendenciaDestacada ? SX_DESTAQUE : undefined}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {p.titulo}
                          </Typography>
                          {p.descricao && (
                            <Typography variant="caption" color="text.secondary">
                              {p.descricao}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{p.responsavel === 'CONSULTOR' ? 'Consultor' : 'Operador'}</TableCell>
                        <TableCell>{p.documentoSolicitado || '—'}</TableCell>
                        <TableCell>
                          {(() => {
                            const lista = anexosPorPendencia.get(p.id) ?? []
                            if (lista.length === 0) {
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              )
                            }
                            return (
                              <Stack spacing={0.75} alignItems="flex-start">
                                <Tooltip title={lista.map((d) => d.nome).join(', ')}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                                    label={lista.length}
                                  />
                                </Tooltip>
                                {/* Vigente + trilha de versões e, na pendência aberta, o substituto (L11/Q38). */}
                                {lista.map((d) => (
                                  <Box key={d.id} aria-label={`Anexo ${d.nome}`} role="group">
                                    <Typography variant="caption" component="div" sx={{ wordBreak: 'break-all' }}>
                                      {d.nome}
                                    </Typography>
                                    <VersoesDocumento documento={d} />
                                    {podeSubstituir(p, d) && (
                                      <Button
                                        size="small"
                                        sx={{ textTransform: 'none', px: 0 }}
                                        aria-label={`Enviar arquivo substituto para ${d.nome}`}
                                        onClick={() => setSubstituindo(d)}
                                      >
                                        Enviar arquivo substituto
                                      </Button>
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            )
                          })()}
                        </TableCell>
                        <TableCell>{fmtData(p.prazoResposta)}</TableCell>
                        <TableCell>
                          <Chip size="small" color={STATUS_COR[p.status]} label={STATUS_LABEL[p.status]} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                            {podeResponder && (
                              <Button
                                size="small"
                                startIcon={<ReplyIcon />}
                                onClick={() => {
                                  setResponderAlvo(p)
                                  setTextoResposta('')
                                }}
                              >
                                Responder
                              </Button>
                            )}
                            {podeOperar &&
                              TRANSICOES[p.status].map((s) => (
                                <Button key={s} size="small" color="inherit" onClick={() => transicionar(p, s)}>
                                  {STATUS_LABEL[s]}
                                </Button>
                              ))}
                            <Tooltip title="Histórico">
                              <Button size="small" color="inherit" startIcon={<HistoryIcon />} onClick={() => abrirHistorico(p)}>
                                Histórico
                              </Button>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Criar */}
      <Dialog open={criarAberto} onClose={() => setCriarAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nova pendência</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Título" value={novoTitulo} onChange={(e) => setNovoTitulo(e.target.value)} required fullWidth />
            <TextField label="Descrição" value={novaDescricao} onChange={(e) => setNovaDescricao(e.target.value)} multiline minRows={2} fullWidth />
            <TextField label="Documento/informação solicitada" value={novoDocumento} onChange={(e) => setNovoDocumento(e.target.value)} fullWidth />
            <TextField
              select
              label="Responsável"
              value={novoResponsavel}
              onChange={(e) => setNovoResponsavel(e.target.value as ResponsavelPendencia)}
              fullWidth
            >
              <MenuItem value="CONSULTOR">Consultor</MenuItem>
              <MenuItem value="OPERADOR">Operador</MenuItem>
            </TextField>
            <TextField
              label="Prazo para resposta"
              type="date"
              value={novoPrazo}
              onChange={(e) => setNovoPrazo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriarAberto(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={criar} variant="contained" disabled={!novoTitulo.trim()}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Responder */}
      {/* Resposta tipada (Fase 6, item 21) — o diálogo muda conforme o que a Originei
          pediu: documento (anexo obrigatório, observação opcional) ou informação (texto
          obrigatório). Nos dois, o consultor atesta que está atualizado antes de enviar. */}
      <Dialog open={!!responderAlvo} onClose={fecharResposta} fullWidth maxWidth="sm">
        <DialogTitle>Responder pendência</DialogTitle>
        <DialogContent>
          {responderAlvo && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {responderAlvo.titulo}
                {responderAlvo.prazoResposta ? ` · prazo ${fmtData(responderAlvo.prazoResposta)}` : ''}
              </Typography>
              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  Solicitação da Originei
                </Typography>
                <Typography variant="body2">
                  {tipoResposta === 'documento'
                    ? <>Anexe o documento atualizado, legível e referente ao período solicitado — <strong>{responderAlvo.documentoSolicitado}</strong>.</>
                    : 'Confirme ou corrija as informações indicadas e registre uma observação para a equipe.'}
                </Typography>
                {responderAlvo.descricao && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {responderAlvo.descricao}
                  </Typography>
                )}
              </Alert>
            </>
          )}
          <TextField
            label={tipoResposta === 'documento' ? 'Observação (opcional)' : 'Sua resposta'}
            value={textoResposta}
            onChange={(e) => setTextoResposta(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            autoFocus={tipoResposta === 'informacao'}
            placeholder={tipoResposta === 'documento'
              ? 'Descreva a complementação realizada, se necessário.'
              : 'Descreva a complementação realizada para a Originei.'}
          />

          <Box sx={{ mt: 2 }}>
            <Button
              component="label"
              startIcon={<AttachFileIcon />}
              size="small"
              variant={tipoResposta === 'documento' ? 'outlined' : 'text'}
            >
              {tipoResposta === 'documento' ? 'Selecionar arquivo' : 'Anexar arquivo'}
              <input
                type="file"
                hidden
                multiple
                onChange={(e) => {
                  const escolhidos = Array.from(e.target.files ?? [])
                  setAnexos((prev) => [...prev, ...escolhidos])
                  // Permite reanexar o mesmo arquivo depois de removê-lo.
                  e.target.value = ''
                }}
              />
            </Button>
            {tipoResposta === 'documento' && anexos.length === 0 && (
              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                O envio exige o documento solicitado: {responderAlvo?.documentoSolicitado}
              </Typography>
            )}
            {anexos.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {anexos.map((a, i) => (
                  <Stack key={`${a.name}-${i}`} direction="row" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      label={a.name}
                      onDelete={() => setAnexos((prev) => prev.filter((_, idx) => idx !== i))}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <FormControlLabel
            sx={{ mt: 2, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={confirmaAtualizado}
                onChange={(e) => setConfirmaAtualizado(e.target.checked)}
                size="small"
                sx={{ pt: 0 }}
              />
            }
            label={
              <Typography variant="body2">
                Confirmo que as informações enviadas estão atualizadas.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharResposta} color="inherit" disabled={enviandoResposta}>
            Cancelar
          </Button>
          <Button
            onClick={responder}
            variant="contained"
            disabled={!respostaPronta || enviandoResposta}
          >
            {enviandoResposta ? 'Enviando…' : 'Enviar resposta'}
          </Button>
        </DialogActions>
      </Dialog>

      <ArquivoSubstitutoDialog
        documento={substituindo}
        enviando={enviandoSubstituto}
        onCancelar={() => setSubstituindo(null)}
        onConfirmar={(arquivo, motivo) => void enviarSubstituto(arquivo, motivo)}
      />

      {/* Histórico */}
      <Dialog open={!!historicoAlvo} onClose={() => setHistoricoAlvo(null)} fullWidth maxWidth="sm">
        <DialogTitle>Histórico de interações</DialogTitle>
        <DialogContent>
          {interacoes.length === 0 ? (
            <Typography color="text.secondary">Sem interações registradas.</Typography>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {interacoes.map((i) => (
                <Box key={i.id}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(i.createdAt).toLocaleString('pt-BR')} · {i.tipo}
                  </Typography>
                  <Typography variant="body2">{i.descricao || '—'}</Typography>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricoAlvo(null)} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PendenciasPage
